/**
 * location domain — policy + geofence configuration (doc 01 A6.4, doc 13 T1).
 * Read `location:read`; mutate `location:manage`. Breadcrumb *trail* reads are a
 * separate, permission-gated concern and deliberately not exposed here.
 * Types mirror the API's location service views (mark for consolidation).
 */
import { del, get, patch, post, put } from './transport.js';
/**
 * THE FOUR RELATIONSHIPS a save may declare to a scheduled revision that is
 * already waiting — the founder's ruling of 2026-09-08, made a wire vocabulary.
 *
 *   keep       the revision stands; this save governs the interval before it.
 *   supersede  the revision is withdrawn and this save replaces it. Needs a reason.
 *   cancel     the revision is withdrawn and nothing replaces it. Needs a reason.
 *   sequence   the revision stands and this save arrives after it.
 *
 * A CLOSED LIST, exported so no screen hard-codes it: a fifth would be a new
 * ruling. OMITTING the declaration when a revision is waiting is refused with a
 * 409 — it is not a fifth relationship and it is not a quiet `keep`.
 */
export const PENDING_RELATIONSHIPS = ['keep', 'supersede', 'cancel', 'sequence'];
/**
 * The REAL validation bounds of `location_policy`, mirrored from the API's
 * `upsertPolicySchema` (apps/api/src/domains/location/schemas.ts) so client
 * validation refuses exactly what the server would refuse — no invented
 * limits, no silently narrower ones.
 */
export const POLICY_BOUNDS = {
    /** 30 seconds to 24 hours. */
    breadcrumbIntervalSeconds: { min: 30, max: 86_400 },
    /** 1 day to 10 years; enforced by the `system.breadcrumb-purge` sweep. */
    breadcrumbRetentionDays: { min: 1, max: 3650 },
    minAccuracyMeters: { min: 1, max: 100_000 },
};
/**
 * The REAL validation bounds of `work_geofence`, mirrored from the API's
 * `createGeofenceSchema`/`updateGeofenceSchema`
 * (apps/api/src/domains/location/schemas.ts), which themselves mirror the
 * table's CHECK constraints. No invented limits, no silently narrower ones.
 */
export const GEOFENCE_BOUNDS = {
    /** Trimmed, 1–200 characters. */
    nameMaxLength: 200,
    latitude: { min: -90, max: 90 },
    longitude: { min: -180, max: 180 },
    /** 1 m to 50 km, whole meters — a geofence is a site, not a region. */
    radiusMeters: { min: 1, max: 50_000 },
};
/**
 * `GET /location/policy/effective` → the version IN FORCE, and nothing else.
 *
 * Kept beside {@link effectivePolicyWithSchedule} rather than replaced by it:
 * most callers genuinely want only "what governs right now" (the worker's
 * disclosure card, a program-override summary, a profile blurb), and widening
 * every one of them to a two-field object would make each restate that it is
 * ignoring the pending half. A caller that must not overwrite a schedule it
 * cannot see uses the other one.
 */
export async function effectivePolicy(query = {}, options) {
    return (await effectivePolicyWithSchedule(query, options)).policy;
}
/**
 * `GET /location/policy/effective` → `{ policy, pending }` — the same single
 * request, with the published-and-waiting change the route also answers with.
 *
 * A settings form MUST use this one. The route answers with the version in
 * force, so a form reading only `policy` re-opens showing values from before a
 * scheduled change and no sign that one exists; saving from there appends a
 * version that governs now, and the pending one still arrives on its own date
 * and replaces it.
 */
export async function effectivePolicyWithSchedule(query = {}, options) {
    const body = await get('/location/policy/effective', { ...options, query: { ...query } });
    // `pending` ABSENT and `pending` NULL are the same answer from this route and
    // are read as one: an older API that does not send the field has no pending
    // change to report, and inventing an "unknown" state a caller cannot act on
    // would put a third branch on every screen for a case the server never emits.
    //
    // `pendingRevisions` reads the same way and lands on `[]`, which is the same
    // claim `pending: null` makes — nothing is waiting on this scope. That is
    // safe HERE and would not be safe on the SAVE path: a screen that read an old
    // server's silence as "nothing is waiting" and then omitted the field would
    // be refused by a current server rather than allowed through, because the
    // decision is made against the series and not against what the client sent.
    return {
        policy: body.policy,
        pending: body.pending ?? null,
        pendingRevisions: body.pendingRevisions ?? [],
    };
}
/** `PUT /location/policy` → `{ policy }` (needs `location:manage`). */
export async function putPolicy(input, options) {
    const body = await put('/location/policy', input, options);
    return body.policy;
}
/**
 * `PUT /location/policy` with the server's own partial-upsert contract
 * (needs `location:manage`).
 *
 * RETURNS BOTH HALVES OF THE SERVER'S ANSWER. This used to return `body.policy`
 * and drop `body.schedule` on the floor — a value plumbed the whole way through
 * the API and read by nobody, which is how a form ends up telling an operator
 * that a change scheduled for next month is live today. The route's own comment
 * says why the two are different facts; the client is where that stopped being
 * true.
 */
export async function upsertPolicy(input, options) {
    return put('/location/policy', input, options);
}
/**
 * `DELETE /location/policy?programId=...` → `{ policy }` (needs
 * `location:manage`). Removes ONE program's override row; the response is the
 * policy that NOW governs the program (org default, or the product default
 * when no org row is stored). `programId` is required on the wire — the
 * org-level row is deliberately not deletable (see the API route header):
 * "back to the default" for the organization is an explicit, audited upsert
 * of the default values, never an absent parameter.
 */
export async function deletePolicy(query, options) {
    const body = await del('/location/policy', {
        ...options,
        query: { programId: query.programId },
    });
    return body.policy;
}
/** `GET /location/geofences` → `{ geofences }`. */
export async function geofences(options) {
    const body = await get('/location/geofences', options);
    return body.geofences;
}
/** `POST /location/geofences` → `{ geofence }` (needs `location:manage`). */
export async function createGeofence(input, options) {
    const body = await post('/location/geofences', input, options);
    return body.geofence;
}
/** `PATCH /location/geofences/:id` → `{ geofence }` (needs `location:manage`). */
export async function updateGeofence(id, input, options) {
    const body = await patch(`/location/geofences/${id}`, input, options);
    return body.geofence;
}
/**
 * `GET /location/exceptions` → the caller-visible exception list (needs
 * `time:read`; visibility is one's own worker plus the supervision edge, or
 * the org-wide `timekeeping:read_all` key).
 */
export async function listExceptions(query = {}, options) {
    return get('/location/exceptions', {
        ...options,
        query: { ...query },
    });
}
/**
 * `POST /location/exceptions/:id/approve` → `{ exception }` (needs
 * `timekeeping:manage` plus reach over the subject worker; never one's own).
 */
export async function approveException(id, note, options) {
    const body = await post(`/location/exceptions/${id}/approve`, note === undefined ? {} : { note }, options);
    return body.exception;
}
/** `POST /location/exceptions/:id/return` → `{ exception }` (same price as approve). */
export async function returnException(id, note, options) {
    const body = await post(`/location/exceptions/${id}/return`, note === undefined ? {} : { note }, options);
    return body.exception;
}
/**
 * `GET /location/issues` → the two derivable detections (needs `time:read`;
 * same visibility as the exception list: one's own worker plus the supervision
 * edge, or the org-wide `timekeeping:read_all` key).
 */
export async function listIssues(query = {}, options) {
    return get('/location/issues', { ...options, query: { ...query } });
}
/**
 * `GET /location/sessions/:id/policy-binding` → what this shift was bound to.
 *
 * PRICED FOR THE DATA SUBJECT, and that is a deliberate decision made on the
 * route (`apps/api/src/domains/location/routes.ts`): `time:read` plus
 * own-worker scoping, NOT the `breadcrumb:view` trail door next to it. This
 * returns policy CONFIGURATION and no coordinates, so charging the movement-
 * trail key would mean a worker could not be told what they had been told.
 *
 * The subject is resolved from the SESSION named in the path, never from
 * request input, and an unresolvable session is a 403 rather than a quiet
 * empty answer.
 */
export async function sessionPolicyBinding(sessionId, options) {
    const body = await get(`/location/sessions/${sessionId}/policy-binding`, options);
    return body.binding;
}
/**
 * `GET /location/me/trail` → what Phibian recorded about where YOU were.
 *
 * PRICED FOR THE DATA SUBJECT AND FOR NOBODY ELSE. It charges
 * `location:view_own_trail` — a key minted by founder ruling 1 of 2026-09-09
 * and seeded to the roles that hold `location:collect`, i.e. the people a trail
 * is collected from. It is NOT `breadcrumb:view`, which reads another person's
 * movements, is granted to no role including `org_admin`, and which this route
 * neither uses nor widens.
 *
 * THERE IS NO WORKER-ID ARGUMENT AND THERE MUST NOT BE ONE. The subject is
 * taken from the signed-in session on the server, as a branded type a request
 * cannot produce, and the route's query schema is `.strict()` — so a widening
 * parameter is a 400 at the boundary rather than something quietly ignored. If
 * a supervisor is ever authorized to read a supervisee's trail that is a
 * different capability with its own key and its own function, not a second
 * argument here.
 *
 * `sessionId` NARROWS: it selects one of the caller's OWN shifts, and naming
 * anybody else's is a 403 with `code: 'not_your_shift'`. Omitted, the answer is
 * about the caller's most recent shift.
 */
export async function myTrail(sessionId, options) {
    const body = await get('/location/me/trail', {
        ...options,
        query: sessionId === undefined ? {} : { sessionId },
    });
    return body.trail;
}
//# sourceMappingURL=location.js.map